import React from 'react';
import { useTranslator, formatCalories } from '@front-activerse/common-components';
import { Row, Col } from 'reactstrap';
import MacroDonutChart from '../../../Layout/Chart/MacroDonutChart';
import { MealMacroSummary } from '../../helpers/mealCalculations';

interface TrackerMealMacroSummaryProps {
  calculatedMealData: MealMacroSummary;
  servings: number;
}

const TrackerMealMacroSummary: React.FC<TrackerMealMacroSummaryProps> = ({
  calculatedMealData,
  servings,
}) => {
  const { t } = useTranslator();

  const formatMacroDisplay = (originalGrams: number, originalPercentage: number) => {
    const multipliedGrams = Math.round(originalGrams * servings);
    if (multipliedGrams === 0 && originalPercentage > 0) return '<1';
    return multipliedGrams;
  };

  const multipliedKcal = Math.round(calculatedMealData.kcal * servings);
  const multipliedMacro = calculatedMealData.macro
    ? {
        protein: {
          grams: formatMacroDisplay(
            calculatedMealData.macro.protein.grams,
            calculatedMealData.macro.protein.percentage
          ),
        },
        fat: {
          grams: formatMacroDisplay(
            calculatedMealData.macro.fat.grams,
            calculatedMealData.macro.fat.percentage
          ),
        },
        carbohydrates: {
          grams: formatMacroDisplay(
            calculatedMealData.macro.carbohydrates.grams,
            calculatedMealData.macro.carbohydrates.percentage
          ),
        },
      }
    : {
        protein: { grams: 0 },
        fat: { grams: 0 },
        carbohydrates: { grams: 0 },
      };

  const proteinPercentage = calculatedMealData.macro?.protein.percentage || 0;
  const fatPercentage = calculatedMealData.macro?.fat.percentage || 0;
  const carbsPercentage = calculatedMealData.macro?.carbohydrates.percentage || 0;

  let missingPercentage = 100;
  if (proteinPercentage > 0 || fatPercentage > 0 || carbsPercentage > 0) {
    missingPercentage = 0;
  }

  return (
    <Row className="align-items-center">
      <Col xs="4">
        <div className="macro-chart-container">
          <MacroDonutChart
            proteinPercentage={calculatedMealData.macro?.protein.percentage || 0}
            fatPercentage={calculatedMealData.macro?.fat.percentage || 0}
            carbsPercentage={calculatedMealData.macro?.carbohydrates.percentage || 0}
            missingPercentage={missingPercentage}
            chartId="tracker-meal"
          />
          <div className="text">
            <p className="title-small semi-bold mb-0">{formatCalories(multipliedKcal)}</p>
            <p className="description-small mb-0">kcal</p>
          </div>
        </div>
      </Col>
      <Col xs="8">
        <Row noGutters>
          <Col>
            <div className="d-flex align-items-start">
              <div className={`bullet protein ${proteinPercentage === 0 && 'empty'}`} />
              <div>
                <p className="description-small mb-0">{t('tracker.macros.proteins')}</p>
                <p className="semi-bold mb-0">{multipliedMacro.protein.grams} g</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className={`bullet fat ${fatPercentage === 0 && 'empty'}`} />
              <div>
                <p className="description-small mb-0">{t('tracker.macros.fats')}</p>
                <p className="semi-bold mb-0">{multipliedMacro.fat.grams} g</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className={`bullet carbs ${carbsPercentage === 0 && 'empty'}`} />
              <div>
                <p className="description-small mb-0">{t('tracker.macros.carbs')}</p>
                <p className="semi-bold mb-0">{multipliedMacro.carbohydrates.grams} g</p>
              </div>
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default TrackerMealMacroSummary;
